import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Clock3,
  Building2,
  ArrowRight,
} from "lucide-react";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const offices = [
  {
    title: "Head Office",
    address:
      "Marble House, Federal Ocean Terminal (FOT), PMB 5474, Onne Port Complex, Rivers State, Nigeria",
    phone: "+234-909-111-1184",
    email: "contact@ogfza.gov.ng",
  },
  {
    title: "Lagos Regional Office",
    address:
      "No 4 Ilu Drive off Ilabere Street off Macpherson Street off Bourdillon Street, Ikoyi, Lagos",
    phone: "+234-909-111-1184",
    email: "contact@ogfza.gov.ng",
  },
  {
    title: "Abuja Liaison Office",
    address: "No. 4 Pope John II Street, Off Gana Street, Maitama, Abuja",
    phone: "+234-909-111-1184",
    email: "contact@ogfza.gov.ng",
  },
  {
    title: "Warri Office",
    address: "Oil & Gas Free Zone, Warri Port Complex, Delta State",
    phone: "+234-909-111-1184",
    email: "contact@ogfza.gov.ng",
  },
];

const ClientServiceLines = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".contact-card", { opacity: 0, y: 60, duration: 1, stagger: 0.12 });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img src={HERO_IMAGE} alt="OGFZA Client Service Lines" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Contact Help
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Client Service Lines
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Access OGFZA service lines for head office, regional offices, investor enquiries, regulatory support and client assistance.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Client Service Lines</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20">
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-14">
            {offices.map((office) => (
              <div
                key={office.title}
                className="contact-card bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-5">
                  <Building2 size={26} />
                </div>

                <h3 className="text-2xl font-black text-[#06183f]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                  {office.title}
                </h3>

                <div className="mt-5 space-y-4 text-slate-600 text-sm leading-relaxed">
                  <p className="flex gap-3">
                    <MapPin size={18} className="text-[#ff6600] flex-shrink-0 mt-1" />
                    {office.address}
                  </p>

                  <p className="flex gap-3">
                    <Phone size={18} className="text-[#ff6600] flex-shrink-0" />
                    {office.phone}
                  </p>

                  <p className="flex gap-3">
                    <Mail size={18} className="text-[#ff6600] flex-shrink-0" />
                    {office.email}
                  </p>

                  <p className="flex gap-3">
                    <Clock3 size={18} className="text-[#ff6600] flex-shrink-0" />
                    Mon - Fri, 09:00am - 05:00pm
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="contact-card bg-[#06183f] rounded-[34px] p-8 md:p-10 text-white grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <p className="text-[#ff6600] font-black tracking-[0.2em] uppercase text-xs mb-3">
                Service Support
              </p>

              <h2 className="text-4xl md:text-5xl font-black leading-tight" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                Need direct customer support?
              </h2>

              <p className="text-white/75 mt-4 max-w-3xl leading-relaxed">
                For official enquiries, documentation support, licence-related questions, and investor guidance, contact OGFZA through the official customer service email.
              </p>
            </div>

            <Link
              to="/contact-help/customer-service"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-[#ff6600] hover:bg-white hover:text-[#06183f] text-white font-black transition-colors"
            >
              Contact Customer Service <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-10">
            <iframe
              title="OGFZA Head Office Map"
              className="w-full h-[430px] rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
              loading="lazy"
              src="https://www.google.com/maps?q=Marble%20House%20Federal%20Ocean%20Terminal%20Onne%20Port%20Complex%20Rivers%20State%20Nigeria&output=embed"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default ClientServiceLines;